import { useAppForm } from '@/lib/use-app-form'
import { Trans, useLingui } from '@lingui/react/macro'
import { useMutation } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { SocialButtons } from '../social-buttons'
import { queryClient, trpc, trpcClient } from '@/main'
import { withViewTransition } from '@/lib/utils'
import { registerSchema } from 'shared/company-form'
import { AuthCard } from '@/routes/_app/-components/shared/auth-card'
import { Separator } from '@/components/ui/separator'

export function CompanyRegisterForm() {
  const { t } = useLingui()
  const { mutateAsync: createUser } = useMutation(trpc.auth.createUser.mutationOptions())

  const form = useAppForm({
    validators: { onChange: registerSchema },
    defaultValues: {
      email: '',
      password: '',
      termsOfUse: false,
    },
    onSubmit: async ({ value }) => {
      await createUser({ ...value, isCompany: true })
      // Fetch the freshly-created user OUT of band (vanilla client → doesn't notify the mounted
      // getUser observer), then publish it into the cache inside a view transition. That makes the
      // re-render that swaps this register form for the company wizard happen *inside* the transition,
      // so the shared flow-frame box morphs (resize + content cross-fade) instead of hard-cutting.
      const user = await trpcClient.auth.getUser.query()
      withViewTransition(() => queryClient.setQueryData(trpc.auth.getUser.queryKey(), user))
    },
  })

  return (
    <AuthCard
      home
      title={t`Company registration`}
      subtitle={t`Create your D'Anchiano account`}
      contentClassName="flex flex-col gap-5"
      footer={
        <>
          {t`Already have an account?`}{' '}
          <Link to="/login" className="text-primary font-medium hover:underline">
            {t`Login`}
          </Link>
        </>
      }
    >
      <SocialButtons register />

      <div className="flex items-center gap-3">
        <Separator className="flex-1" />
        <span className="text-muted-foreground text-xs">{t`or`}</span>
        <Separator className="flex-1" />
      </div>

      <form
        onSubmit={(event) => {
          event.preventDefault()
          form.handleSubmit()
        }}
        className="flex flex-col gap-3"
      >
        {/* type="email" + autoComplete="username" pairs with the new-password field below so
                Chrome recognizes this as a sign-up form and offers to save the credentials (matches
                the applicant/employee register forms). */}
        <form.AppField
          name="email"
          children={(field) => <field.TextField label={t`Email`} type="email" autoComplete="username" />}
        />
        <form.AppField
          name="password"
          children={(field) => <field.PasswordField label={t`Password`} autoComplete="new-password" />}
        />
        <form.AppField
          name="termsOfUse"
          children={(field) => (
            <field.CheckboxField
              className="my-3"
              label={
                <div>
                  <Trans>
                    I accept the{' '}
                    <Link to="/terms-of-use" className="underline" target="_blank" rel="noopener noreferrer">
                      Terms of use
                    </Link>{' '}
                    and the{' '}
                    <Link to="/privacy-policy" className="underline" target="_blank" rel="noopener noreferrer">
                      Privacy Policy
                    </Link>
                  </Trans>
                </div>
              }
            />
          )}
        />

        <form.AppForm>
          <form.SubmitButton label={t`Create account`} />
        </form.AppForm>
      </form>
    </AuthCard>
  )
}
